import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ArrowDownRight,
  ArrowUpLeft,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  MoveDiagonal,
  Settings as SettingsIcon,
  Palette,
  Shuffle,
} from 'lucide-react';
import { levels, type LevelDef } from '@/config/levels.config';
import { blockersOf, groups, modes, type ModeDef } from '@/config/modes.config';
import { gameConfig } from '@/config/game.config';
import {
  createRng,
  startSession,
  answer as answerFn,
  next as nextFn,
  generate,
  type Question,
  type Session,
} from '@/engine';
import { rulesFor } from '@/engine/fromConfig';
import { useSettings } from '@/store/settings';
import { recordKey, useRecords } from '@/store/records';
import { TermBlock, TermOp } from '@/ui/TermBlock';
import { SuitIcon } from '@/ui/ComboCards';
import { AnswerGrid } from '@/ui/AnswerGrid';
import { HintButton, HintPanel } from '@/ui/HintTable';
import { Modal } from '@/ui/Modal';
import { SettingsScreen } from './Settings';
import { preload, sfx } from '@/lib/sound';
import { track } from '@/services/events';
import { useAccess } from '@/services/entitlement';

export function PlayScreen() {
  const { modeId, levelId } = useParams();
  const mode = modes.find((x) => x.id === modeId);
  const level = levels.find((x) => x.id === levelId);
  const access = useAccess(`${modeId}:${levelId}`);
  const [params] = useSearchParams();
  const seed = params.get('seed');
  if (!mode || !level || !access.allowed) return <Navigate to="/" replace />;
  return (
    <Round
      key={`${mode.id}:${level.id}`}
      mode={mode}
      level={level}
      seed={seed ? Number(seed) : undefined}
    />
  );
}

function Round({
  mode,
  level,
  seed,
}: {
  mode: ModeDef;
  level: LevelDef;
  seed?: number | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bMax = useSettings((s) => s.bMaxFor(mode.id, level.id));
  const cardBlockers = useSettings((s) => s.blockersFor(mode.id, level.id));
  const blockers = useMemo(() => blockersOf(mode, cardBlockers), [mode, cardBlockers]);
  const rules = useMemo(() => rulesFor(mode, level, bMax, blockers), [mode, level, bMax, blockers]);
  const levelKey = `${mode.id}:${level.id}`;
  // one RNG per round component, held in state (never replaced) so it is stable across renders
  const [rng] = useState(() => (seed === undefined ? createRng() : createRng(seed)));
  const [hintPinned, setHintPinned] = useState(false);
  const showA = useSettings((s) => s.showA);
  const showB = useSettings((s) => s.showB);
  const setShowA = useSettings((s) => s.setShowA);
  const setShowB = useSettings((s) => s.setShowB);
  const fillDiagonal = useSettings((s) => s.fillDiagonal);
  const fillReversed = useSettings((s) => s.fillReversed);
  const fillRandom = useSettings((s) => s.fillRandom);
  const showSuits = useSettings((s) => s.showSuits);
  const setShowSuits = useSettings((s) => s.setShowSuits);
  const suitColors = useSettings((s) => s.suitColors);
  const setSuitColors = useSettings((s) => s.setSuitColors);
  const setFillDiagonal = useSettings((s) => s.setFillDiagonal);
  const setFillReversed = useSettings((s) => s.setFillReversed);
  const setFillRandom = useSettings((s) => s.setFillRandom);
  const sound = useSettings((s) => s.sound);
  const soundTheme = useSettings((s) => s.soundTheme);
  useEffect(() => {
    if (sound) preload(soundTheme);
  }, [sound, soundTheme]);
  const [startedAt] = useState(() => Date.now());
  const [session, setSession] = useState<Session>(() => startSession(rules, rng));
  const key = recordKey(mode.id, level.id, bMax);
  const submit = useRecords((s) => s.submit);

  // question history for ← →: every question started so far (answered or skipped); `view` is the index under review, null = live
  const [past, setPast] = useState<{ question: Question; picked: number | null }[]>([]);
  const [view, setView] = useState<number | null>(null);

  const pick = useCallback(
    (i: number) => {
      if (session.phase !== 'question') return;
      setPast((p) => [...p, { question: session.question, picked: i }]);
      setSession((s) => {
        const n = answerFn(s, i, rules);
        if (n !== s && sound) (i === s.question.correct ? sfx.correct : sfx.wrong)(soundTheme);
        return n;
      });
    },
    [session.phase, session.question, rules, sound, soundTheme],
  );
  const prev = useCallback(
    () =>
      setView((v) => {
        const i = (v ?? past.length) - 1;
        return i >= 0 ? i : v;
      }),
    [past.length],
  );
  const next = useCallback(() => {
    if (view !== null) {
      setView(view + 1 < past.length ? view + 1 : null);
      return;
    }
    if (session.phase !== 'question') return;
    // live: skip to a fresh question; the skipped one stays in the history unanswered
    setPast((p) => [...p, { question: session.question, picked: null }]);
    setSession((s) => ({ ...s, question: generate(rules.gen, rng) }));
  }, [view, past.length, session.phase, session.question, rules, rng]);

  useEffect(() => {
    track({ name: 'round_started', level: levelKey, bMax });
  }, [levelKey, bMax, startedAt]);

  // feedback → next question after a short pause
  useEffect(() => {
    if (session.phase !== 'feedback') return undefined;
    const id = setTimeout(() => setSession((s) => nextFn(s, rules, rng)), gameConfig.feedbackMs);
    return () => clearTimeout(id);
  }, [session.phase, rules, rng]);

  // a round has no end of its own: the player leaves with Esc or the back button, and the score is recorded then
  const latest = useRef(session);
  useEffect(() => {
    latest.current = session;
  }, [session]);
  useEffect(
    () => () => {
      const s = latest.current;
      if (s.answered === 0) return;
      track({
        name: 'round_finished',
        level: levelKey,
        bMax,
        score: s.score,
        solved: s.solved,
        answered: s.answered,
        durationMs: Date.now() - startedAt,
      });
      submit(key, s.score);
    },
    [levelKey, bMax, key, submit, startedAt],
  );

  const { pathname, hash } = useLocation();
  const closeModal = useCallback(() => navigate(pathname, { replace: true }), [navigate, pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (hash) return; // a modal is open: it owns Esc
      if (e.key === 'Escape') navigate(`/m/${mode.id}`);
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (/^[1-4]$/.test(e.key) && view === null && session.phase === 'question')
        pick(Number(e.key) - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, pick, prev, next, view, session.phase, mode.id, hash]);

  // what is on screen: a past question under review, or the live one
  const shown = view === null ? null : past[view];
  const q = shown ? shown.question : session.question;
  const picked = shown ? shown.picked : session.picked;
  // random mode: a 50/50 direction per question, hashed from the question and its number so it is stable while shown
  // (the live question's number is its index in the history: it is appended there in the same event that answers it)
  const qNumber = view ?? (session.phase === 'feedback' ? past.length - 1 : past.length);
  const coin = useMemo(() => {
    let h = 2166136261;
    for (const ch of `${qNumber}:${q.terms.map((t) => `${t.a}x${t.b}${t.sign}`).join(',')}`) {
      h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
    }
    return ((h >>> 0) & 1) === 1;
  }, [q, qNumber]);
  // toggling random mid-question keeps the direction already on screen; the coin (or the arrow setting) applies from the next question
  const [hold, setHold] = useState<{ q: typeof q; reversed: boolean } | null>(null);
  const reversed = hold && hold.q === q ? hold.reversed : fillRandom ? coin : fillReversed;
  const fill = fillDiagonal ? 'diagonal' : 'column';
  const showLines = groups.find((g) => g.id === mode.group)?.blockers === 'custom';
  const live =
    picked === null
      ? ''
      : picked === q.correct
        ? t('play.liveOk')
        : t('play.liveBad', { value: q.value });

  return (
    <section className="flex flex-1 flex-col gap-7">
      {/* HUD */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-6">
          <button
            type="button"
            title={t('common.back')}
            onClick={() => navigate(`/m/${mode.id}`)}
            className="grid size-11 place-items-center rounded-xl border border-line-strong bg-surface text-fg-muted hover:border-accent hover:text-accent"
          >
            <ChevronLeft className="size-[22px]" strokeWidth={2.4} />
          </button>
          <div className="flex flex-col gap-1">
            <span className="label">{t('play.mode')}</span>
            <span className="num pt-1.5 font-display text-[22px] leading-none font-black">
              {mode.group && `${t(`group.${mode.group}.name`)} · `}
              {t(`mode.${mode.id}.name`)} · {t(`level.${level.id}.name`)} · {bMax}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {(
            [
              [
                'prev',
                t('play.prev'),
                prev,
                (view ?? past.length) === 0,
                <ChevronsLeft className="size-[22px]" strokeWidth={1.8} />,
              ],
              [
                'next',
                t('play.next'),
                next,
                view === null && session.phase !== 'question',
                <ChevronsRight className="size-[22px]" strokeWidth={1.8} />,
              ],
            ] as const
          ).map(([key, title, go, off, icon]) => (
            <button
              key={key}
              type="button"
              title={title}
              disabled={off}
              onClick={go}
              className="grid size-11 place-items-center self-center rounded-xl border border-line-strong bg-surface text-fg-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-default disabled:opacity-40 disabled:hover:border-line-strong disabled:hover:text-fg-muted"
            >
              {icon}
            </button>
          ))}
          {(
            [
              ['A', showA, setShowA, 'va'],
              ['B', showB, setShowB, 'vb'],
            ] as const
          ).map(([letter, on, set, color]) => (
            <button
              key={letter}
              type="button"
              title={t(`play.show${letter}`)}
              aria-pressed={on}
              onClick={() => set(!on)}
              className={`grid size-11 place-items-center self-center rounded-xl border border-line-strong bg-surface font-display text-[20px] leading-none font-black transition-colors hover:border-accent aria-pressed:border-accent ${on ? color : 'text-fg-muted'}`}
            >
              {letter}
            </button>
          ))}
          {(
            [
              [
                'showSuits',
                showSuits,
                setShowSuits,
                <span aria-hidden className="hud-suit">
                  <SuitIcon suit="s" />
                </span>,
              ],
              [
                'suitColors',
                suitColors,
                setSuitColors,
                <Palette className="size-[22px]" strokeWidth={1.8} />,
              ],
              [
                'fillReversed',
                fillReversed,
                (on: boolean) => {
                  setHold(null);
                  setFillReversed(on);
                },
                fillReversed ? (
                  <ArrowUpLeft className="size-[22px]" strokeWidth={1.8} />
                ) : (
                  <ArrowDownRight className="size-[22px]" strokeWidth={1.8} />
                ),
              ],
              [
                'fillDiagonal',
                fillDiagonal,
                setFillDiagonal,
                <MoveDiagonal className="size-[22px]" strokeWidth={1.8} />,
              ],
              [
                'fillRandom',
                fillRandom,
                (on: boolean) => {
                  setHold({ q, reversed });
                  setFillRandom(on);
                },
                <Shuffle className="size-[22px]" strokeWidth={1.8} />,
              ],
            ] as const
          ).map(([key, on, set, icon]) => (
            <button
              key={key}
              type="button"
              title={t(`play.${key}`)}
              aria-pressed={on}
              onClick={() => set(!on)}
              className="grid size-11 place-items-center self-center rounded-xl border border-line-strong bg-surface text-fg-muted transition-colors hover:border-accent hover:text-accent aria-pressed:border-accent aria-pressed:text-accent"
            >
              {icon}
            </button>
          ))}
          <HintButton
            bMax={bMax}
            terms={q.terms}
            a={rules.gen.a}
            pinned={hintPinned}
            onToggle={() => setHintPinned((p) => !p)}
          />
          <Link
            to="#settings"
            title={t('home.settings')}
            className="grid size-11 place-items-center self-center rounded-xl border border-line-strong bg-surface text-fg-muted transition-colors hover:border-accent hover:text-accent"
          >
            <SettingsIcon className="size-[22px]" strokeWidth={1.8} />
          </Link>
        </div>
      </div>

      {/* stage */}
      <div
        className="stage flex flex-1 flex-col items-center justify-center gap-[clamp(14px,2.5vh,28px)] py-2"
        style={{ '--stage-w': `${STAGE_WIDTH[level.terms] ?? 1500}px` } as React.CSSProperties}
      >
        <div
          key={`${view ?? 'live'}-${qNumber}-${session.phase === 'question'}`}
          className="terms flex flex-wrap items-end justify-center gap-x-[clamp(10px,1.6vw,24px)] gap-y-4 max-md:flex-col max-md:items-center"
          style={{ '--n': q.terms.length } as React.CSSProperties}
          aria-live="polite"
        >
          {q.terms.map((term, i) => (
            <TermBlockWithOp
              key={i}
              index={i}
              term={term}
              region={mode.region}
              showA={showA}
              showB={showB}
              fill={fill}
              reversed={reversed}
              showLines={showLines}
            />
          ))}
          <TermOp>=</TermOp>
        </div>
        <AnswerGrid question={q} picked={picked} onPick={pick} readOnly={view !== null} />
        <HintPanel
          bMax={bMax}
          terms={q.terms}
          a={rules.gen.a}
          open={hintPinned}
          onClose={() => setHintPinned(false)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-[12px] tracking-[0.1em] text-fg-dim uppercase">
        <span>
          <Kbd>1</Kbd> <Kbd>2</Kbd> <Kbd>3</Kbd> <Kbd>4</Kbd> {t('play.hintAnswer')}
          {'  '}
          <Kbd>←</Kbd> <Kbd>→</Kbd> {t('play.hintNav')}
          {view !== null && ` · ${view + 1} / ${past.length + 1}`}
        </span>
        <span>
          <Kbd>Esc</Kbd> {t('play.hintQuit')}
        </span>
      </div>
      <div className="sr-only" aria-live="assertive">
        {live}
      </div>

      {hash === '#settings' && (
        <Modal labelledBy="settings-title" onClose={closeModal}>
          <SettingsScreen onClose={closeModal} />
        </Modal>
      )}
    </section>
  );
}

/** width of the question zone at 1× per term count (the row of term blocks, or the answer grid): drives the big-screen scale */
const STAGE_WIDTH: Record<number, number> = { 1: 900, 2: 1150, 3: 1500 };

function TermBlockWithOp({
  index,
  term,
  region,
  showA = true,
  showB = true,
  fill = 'column',
  reversed = false,
  showLines = false,
}: { index: number } & Parameters<typeof TermBlock>[0]) {
  return (
    <>
      {index > 0 && <TermOp>{term.sign === '-' ? '−' : '+'}</TermOp>}
      <TermBlock
        term={term}
        region={region}
        showA={showA}
        showB={showB}
        fill={fill}
        reversed={reversed}
        showLines={showLines}
      />
    </>
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="rounded-md border border-line-strong px-1.5 font-mono text-[11px] text-fg-muted">
      {children}
    </kbd>
  );
}
